import type { ComponentProps } from 'react';

import { Icon, type AppIconName } from '../../icons';
import { cn } from '../../shared';
import { severityClasses, type Severity } from './severity';

export const alertVariants = (severity: Severity = 'neutral'): string =>
  `px-alert-x py-alert-y gap-inline text-body flex rounded-md border-l-4 ${severityClasses(severity)}`;

/** `off` by default: a live region announced on every page load interrupts for no reason. */
export type AlertLiveness = 'off' | 'polite' | 'assertive';

const LIVE_ROLE = { off: undefined, polite: 'status', assertive: 'alert' } as const;

export interface AlertProps extends Omit<ComponentProps<'div'>, 'role'> {
  variant?: Severity;
  live?: AlertLiveness;
  icon?: AppIconName;
}

/**
 * Colour alone does not convey the severity (WCAG 1.4.1), so the message has to
 * say it. The icon and the fill are reinforcement.
 */
export function Alert({ variant, live = 'off', icon, className, children, ...props }: AlertProps) {
  return (
    <div role={LIVE_ROLE[live]} className={cn(alertVariants(variant), className)} {...props}>
      {icon ? <Icon name={icon} aria-hidden="true" className="mt-0.5 shrink-0" /> : null}
      <div className="gap-field-label flex min-w-0 flex-col">{children}</div>
    </div>
  );
}

export function AlertTitle({ className, ...props }: ComponentProps<'p'>) {
  return <p className={cn('font-semibold', className)} {...props} />;
}

export function AlertDescription({ className, ...props }: ComponentProps<'p'>) {
  return <p className={cn('text-caption', className)} {...props} />;
}
